// Permission codes and the one role matrix (modules-and-api.md §0.5, §2.2; requirements TEN-31, TEN-66, R-03, R-04).
// `<module>.<resource>.<action>` — one flat namespace. Never a role name in a route guard.
//
// Every code that appears in a §5 Perm column is here, and every code here appears in the route inventory
// (`openapi/inventory.ts`) — `test/permissions.test.ts` holds both directions. Codes are added, never renamed.
// The matrix was confirmed as written by the owner on 15 Sep 2026 (D38); what a Branch Owner holds beyond their role is a
// designation grant (`DESIGNATION_GRANTS`, D39 and D40), never a seventh role.

export const ROLES = ["Owner", "Admin", "Optometrist", "Dispenser", "FrameConsultant", "Receptionist"] as const;
export type Role = (typeof ROLES)[number];
/** Cross-tenant roles. FranchisorAdmin acts on a franchisee (TEN-08); PlatformAdmin lives outside every tenant (TEN-03). */
export type ExtendedRole = Role | "FranchisorAdmin" | "PlatformAdmin";

/** R-03: Owner ⊇ Admin ⊇ (Receptionist ∪ FrameConsultant ∪ Dispenser) for operational codes. */
const OPERATIONAL: readonly Role[] = ["Owner", "Admin", "Receptionist", "FrameConsultant", "Dispenser", "Optometrist"];
const ADMIN: readonly Role[] = ["Owner", "Admin"];
const OWNER: readonly Role[] = ["Owner"];
/** R-04: clinical data view/edit — Owner, Admin, Optometrist, Dispenser. Receptionist none (TEN-66). */
const CLINICAL_STAFF: readonly Role[] = ["Owner", "Admin", "Optometrist", "Dispenser"];
/** R-04: as CLINICAL_STAFF plus Frame Consultant, who reads prescriptions for dispensing and nothing else. */
const CLINICAL_READ: readonly Role[] = ["Owner", "Admin", "Optometrist", "Dispenser", "FrameConsultant"];
/** R-04: stock receive, PO and lab-order create/send — every role except Receptionist. */
const STOCK_ROLES: readonly Role[] = ["Owner", "Admin", "Optometrist", "Dispenser", "FrameConsultant"];
/** R-03: clinical sign-off codes require role Optometrist explicitly; the guard ORs `principal.isClinician` (§2.2). */
const OPTOMETRIST: readonly Role[] = ["Optometrist"];

/** The matrix. Extend per module in its own sprint; codes are added, never renamed. */
export const MATRIX = {
  // tenancy
  "tenancy.settings.read": OPERATIONAL,
  "tenancy.settings.write": OWNER,                    // a Branch Owner holds it at the scope of a branch they own (D40, DESIGNATION_GRANTS)
  "tenancy.privacy.write": OWNER,
  "tenancy.flags.write": OWNER,
  "tenancy.branch.read": OPERATIONAL,
  "tenancy.branch.write": ADMIN,
  "tenancy.franchise.read": ADMIN,
  "tenancy.export.request": OWNER,
  // identity
  "identity.user.read": ADMIN,
  "identity.user.write": ADMIN,
  "identity.user.reset_pin": ADMIN,
  "identity.session.read": ADMIN,
  "identity.api_client.write": OWNER,
  // governance
  "governance.audit.read": ADMIN,
  "governance.audit.export": ADMIN,
  "governance.control_log.read": ADMIN,               // a Branch Owner holds it for the branches they own (D39, DESIGNATION_GRANTS)
  "governance.control_log.export": ADMIN,
  "governance.access_log.read": ADMIN,
  "governance.consent.write": OPERATIONAL,
  "governance.dsar.manage": ADMIN,
  // platform (PlatformAdmin principal only — never granted to a tenant role)
  "platform.tenant.manage": [],
  "platform.tenant.shard": [],
  "platform.support.request": [],
  "platform.franchise.manage": [],
  "platform.reference.write": [],
  "platform.health.read": [],
  // patients (§5.6; R-04 "Patient demographics create/edit, search: all tenant roles")
  "patients.patient.read": OPERATIONAL,
  "patients.patient.write": OPERATIONAL,
  "patients.patient.change_status": OPERATIONAL,      // `Prohibited` additionally needs step-up (TEN-48)
  "patients.patient.merge": ADMIN,                    // PAT-65: Owner/Admin with Control PIN
  "patients.patient.delete": ADMIN,                   // PAT-56: only a patient with no history (PAT-55), with Control PIN; and Deleted records
  "patients.family.manage": OPERATIONAL,
  "patients.medical_aid.write": OPERATIONAL,
  "patients.document.read": OPERATIONAL,
  "patients.document.write": OPERATIONAL,
  "patients.document.delete": OPERATIONAL,            // any role may initiate, always with step-up (R-04, PAT-29)
  "patients.registration.handover": OPERATIONAL,      // kiosk handover is a front-desk action (TEN-65, BR-21)
  "patients.registration.review": OPERATIONAL,
  "patients.reference.write": ADMIN,
  // appointments (§5.7; R-04 "Appointments: all roles")
  "appointments.appointment.read": OPERATIONAL,
  "appointments.appointment.write": OPERATIONAL,
  "appointments.type.write": ADMIN,
  "appointments.recall.read": OPERATIONAL,
  "appointments.recall.write": OPERATIONAL,
  // clinical (§5.9; TEN-66, R-04)
  "clinical.prescription.read": CLINICAL_READ,
  "clinical.prescription.write": CLINICAL_STAFF,      // Dispenser may enter a draft on behalf of an Optometrist (R-04)
  "clinical.prescription.finalise": OPTOMETRIST,      // clinical code: Optometrist or isClinician (R-03, §2.2)
  "clinical.prescription.void": CLINICAL_STAFF,       // Draft/Final only, always with step-up (BR-05)
  "clinical.examination.write": OPTOMETRIST,          // clinical code: "edit limited to Optometrist for exam findings" (R-04)
  "clinical.card.read": CLINICAL_STAFF,
  "clinical.card.write": CLINICAL_STAFF,
  "clinical.equipment.read": CLINICAL_STAFF,
  "clinical.equipment.write": CLINICAL_STAFF,
  "clinical.referrer.write": CLINICAL_STAFF,
  "clinical.referral.write": CLINICAL_STAFF,
  "clinical.reference.write": ["Owner", "Admin", "Optometrist"],   // ICD-10 favourites and derivation rules need clinical review (OPN-62)
  "clinical.template.write": ["Owner", "Admin", "Optometrist"],    // card and exam template libraries (CLN-50, CLN-56)
  "clinical.renewal.read": OPERATIONAL,               // the contact-lens renewal worklist is derived from order lines (TEN-66)
  // catalogue (§5.10)
  "catalogue.product.read": OPERATIONAL,
  "catalogue.product.write": STOCK_ROLES,
  "catalogue.product.delete": ADMIN,                  // only when unreferenced, with step-up (CAT-07, R-24)
  "catalogue.rules.write": ADMIN,
  "catalogue.settings.write": ADMIN,
  // pricing (§5.11) — the engines are read by everyone who quotes; the rate tables are configuration
  "pricing.price.read": OPERATIONAL,
  "pricing.branch_price.write": ADMIN,
  "pricing.markup.write": ADMIN,
  "pricing.price_list.write": ADMIN,
  "pricing.tariff.write": ADMIN,
  "pricing.discount.write": ADMIN,
  "pricing.fitting_fee.write": ADMIN,
  // inventory (§5.12; R-04 stock roles)
  "inventory.stock.read": OPERATIONAL,
  "inventory.stock.write": STOCK_ROLES,
  "inventory.stock.reserve": ADMIN,                   // internal surface used by sales; humans reach it only for support (CAT-84)
  "inventory.serial.read": OPERATIONAL,
  "inventory.serial.write": STOCK_ROLES,
  "inventory.serial.delete": ADMIN,
  "inventory.adjustment.write": STOCK_ROLES,          // negative adjustments additionally need step-up (CAT-67)
  "inventory.stock_take.read": STOCK_ROLES,
  "inventory.stock_take.write": STOCK_ROLES,
  "inventory.stock_take.finalise": STOCK_ROLES,       // always with step-up (CAT-68, R-21)
  "inventory.transfer.write": STOCK_ROLES,
  "inventory.label.print": STOCK_ROLES,
  // purchasing (§5.8 doc4, §5.12, §5.15; R-04 "PO/lab order create/send") — the receipt without a purchase order reuses the PO codes (W9)
  "purchasing.po.read": STOCK_ROLES,
  "purchasing.po.write": STOCK_ROLES,
  // W6 (Sprint 4, a D38 matrix change for Khaalid to confirm): the supplier master is read by every role and kept by Owner and
  // Admin (BR-PURCHASING-011).
  "purchasing.supplier.read": OPERATIONAL,
  "purchasing.supplier.write": ADMIN,
  // imports (§5.21; W6, W8): the stock roles run imports, and every route on an import checks its kind's own code as well, so a
  // price-list import stays Owner and Admin (`pricing.price_list.write`, BR-IMPORTS-001); saved mapping profiles are Owner and Admin.
  "imports.job.read": STOCK_ROLES,
  "imports.job.write": STOCK_ROLES,
  "imports.profile.write": ADMIN,
  // sales (§5.8 doc4, §5.9; R-04 "Quotes/orders create/edit: all roles")
  "sales.quote.read": OPERATIONAL,
  "sales.order.read": OPERATIONAL,
  "sales.order.write": OPERATIONAL,
  // billing (§5.8 doc4; R-04 "post invoice: all roles; capture payment: all roles")
  "billing.invoice.read": OPERATIONAL,
  "billing.credit_note.read": OPERATIONAL,
  "billing.receipt.read": OPERATIONAL,
  "billing.statement.read": OPERATIONAL,
  // lab orders (§5.8 doc4)
  "lab_orders.order.read": STOCK_ROLES,
  // cash-up (§5.8 doc4; R-04 "Cash-up perform: all roles")
  "cash_up.cash_up.read": OPERATIONAL,
  // analytics (§5.8 doc4; R-04 "Reports: Owner, Admin all branches")
  "analytics.month_end_pack.read": ADMIN,
  // documents (§5.8)
  "documents.document.read": OPERATIONAL,
  "documents.document.write": OPERATIONAL,
  "documents.document.deliver": OPERATIONAL,
  "documents.document.print": OPERATIONAL,
  "documents.template.write": ADMIN,
  "documents.render.manage": ADMIN,
} as const satisfies Record<string, readonly Role[]>;

export type PermissionCode = keyof typeof MATRIX;
export const PERMISSION_CODES = Object.keys(MATRIX) as PermissionCode[];

/**
 * Clinical codes (R-03, §2.2): the role list names Optometrist only, and the guard grants them to any principal
 * with `isClinician` as well — how an owner-optometrist signs a prescription without a second account (Q-08).
 */
export const CLINICAL_CODES: ReadonlySet<PermissionCode> = new Set<PermissionCode>([
  "clinical.prescription.finalise",
  "clinical.examination.write",
]);

/**
 * What a Franchisor Admin may exercise against a linked franchisee (TEN-08, R-02, §2.2(b)): reports, the audit and
 * Control logs, and franchisee user administration — and nothing else. Never patient or clinical data.
 */
export const FRANCHISOR_CODES: ReadonlySet<PermissionCode> = new Set<PermissionCode>(
  PERMISSION_CODES.filter(
    (code) =>
      code.startsWith("analytics.") ||
      code.startsWith("identity.user.") ||
      code === "governance.audit.read" ||
      code === "governance.control_log.read",
  ),
);

/** Codes only a PlatformAdmin principal holds; their role list is empty by construction (TEN-03, R-04). */
export const PLATFORM_CODES: ReadonlySet<PermissionCode> = new Set<PermissionCode>(
  PERMISSION_CODES.filter((code) => code.startsWith("platform.")),
);

/**
 * Designations a tenant user carries beside their role. `BranchOwner` lives on a branch assignment (TEN-24, Q-16) and is
 * never a role: it names who authorises step-ups and approves cash-ups at that branch and, since D39 and D40, what the
 * holder may do there beyond their role.
 */
export const DESIGNATIONS = ["BranchOwner"] as const;
export type Designation = (typeof DESIGNATIONS)[number];

/**
 * What a designation grants beyond the holder's role — always within the branches the designation is held in, never for
 * the organisation: a Branch Owner reads the Control Log of the branches they own (D39) and writes the setting overrides
 * at those branches' scope (D40). The guard lets a designation grant through only where the route names the branch it
 * addresses or keeps its listing to the granted branches; an `org`-scoped route and the exports never pass on one.
 */
export const DESIGNATION_GRANTS = {
  BranchOwner: ["governance.control_log.read", "tenancy.settings.write"],
} as const satisfies Record<Designation, readonly PermissionCode[]>;

export function designationHas(designation: Designation, code: PermissionCode): boolean {
  return (DESIGNATION_GRANTS[designation] as readonly PermissionCode[]).includes(code);
}

export function roleHas(role: Role, code: PermissionCode): boolean {
  return (MATRIX[code] as readonly Role[]).includes(role);
}

/**
 * The one place a role is compared (§2.2(a)). Clinical codes are also granted to a clinician principal;
 * platform codes are granted to nobody here — the PlatformAdmin principal is recognised before this check.
 */
export function principalHas(principal: { role: Role; isClinician?: boolean }, code: PermissionCode): boolean {
  if (roleHas(principal.role, code)) return true;
  return CLINICAL_CODES.has(code) && principal.isClinician === true;
}

/**
 * TEN-32 / R-03, BR-IDENTITY-034: who may administer a user of a given role, beside holding the code. An Owner administers
 * anyone, another Owner included; nobody else administers an Owner — not to create, promote, edit, reassign, deactivate or
 * reactivate one, reset their PIN, resend their invitation, clear their locks, force their password reset or revoke their
 * sessions. The API's user administration and the Users screen ask this one rule.
 */
export function mayAdministerRole(actor: string, target: string): boolean {
  if (actor === "Owner") return true;
  return target !== "Owner";
}
